import LineChart from '@utils/charts/LineChart.js'
import { defaultDataSet } from '@meta/lineChartDatasetDefaults.js'
import { demoDataSet, demoLabels } from '@meta/lineChartDatasetDemo.js'
import { differenceInDays, parseISO } from 'date-fns'
import { mapGetters, mapState } from 'vuex'

export default {
  name: 'Stats',
  inject: ['$parseDateTime'],
  components: { LineChart },
  data() {
    return {
      targetStats: 'impressions',
      defaultDataSet: defaultDataSet,
      dataCollection: {
        labels: demoLabels,
        datasets: demoDataSet,
      },
      chartDataChanged: false,
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        pointDotRadius: 1,
        animations: {
          radius: {
            duration: 400,
            easing: 'linear',
            loop: (context) => context.active,
          },
        },
        legend: {
          labels: false,
          // onHover: function (e) {
          //   e.target.style.cursor = 'pointer'
          // },
        },
        hover: {
          // mode: null
          onHover: function (e) {
            const point = this.getElementAtEvent(e)
            if (point.length) e.target.style.cursor = 'pointer'
            else e.target.style.cursor = 'default'
          },
        },
        tooltips: {
          backgroundColor: '#0f2815',
          callbacks: {
            label: function (tooltipItem, data) {
              // let label = data.datasets[tooltipItem.datasetIndex].label || '';
              return '' + tooltipItem.yLabel
            },
          },
        },
        scales: {
          // https://www.chartjs.org/docs/latest/axes/styling.html
          xAxes: [
            {
              ticks: {
                maxRotation: 0,
                minRotation: 0,
                fontColor: '#7c8086',
                fontStyle: '400',
                callback: function (tick, index, array) {
                  if (array.length > 7) {
                    return index % 3 ? '' : tick
                  }
                }
              },
              gridLines: {
                display: false,
              },
            },
          ],
          yAxes: [
            {
              ticks: {
                min: 0, // ignoring negative steps
                padding: 20,
                beginAtZero: true,
                stepSize: 2,
                fontColor: '#7c8086',
                fontStyle: '500',
                // Keep ticks from stacking up too close
                callback: function (tick, index, array) {
                  if (array.length > 7) {
                    return index % 3 ? '' : tick
                  }
                  return tick
                },
              },
              gridLines: {
                tickMarkLength: 0,
                display: true,
                drawBorder: false,
                lineWidth: 1,
              },
            },
          ],
        },
      },
      chartStyles: {
        height: '259px',
      },
    }
  },
  computed: {
    ...mapGetters('campaign/analytics/stats', [
      'allCampaignImpressionsByUUID',
      'allCampaignConversionsByUUID',
      'allCampaignLeadsByUUID',
    ]),
    ...mapState({
      targetCampaignUUID: (state) => state.campaign.targetCampaignUUID,
      getStatsLoading: (state) => state.campaign.analytics.stats.getStatsLoading,
      getStatsCompleted: (state) => state.campaign.analytics.stats.getStatsCompleted,
      getStatsError: (state) => state.campaign.analytics.stats.getStatsError,
      targetCampaignStats: (state) =>
        state.campaign.analytics.stats.campaignStatsObject[state.campaign.targetCampaignUUID],
      analyticsTimeFrame: (state) => state.campaign.analytics.stats.timeFrame,
    }),
    targetCampaign() {
      return this.$store.getters['campaign/targetCampaign']
    },
    totalImpressions() {
      if (!this.targetCampaignUUID || !this.getStatsCompleted) {
        return 233
      }

      return this.allCampaignImpressionsByUUID(this.targetCampaignUUID)
    },
    totalConversions() {
      if (!this.targetCampaignUUID || !this.getStatsCompleted) {
        return 100
      }

      return this.allCampaignConversionsByUUID(this.targetCampaignUUID)
    },
    totalCTR() {
      const totalPercentage = (this.totalConversions / this.totalImpressions) *
        100

      if (isNaN(totalPercentage)) {
        return 0
      }

      return parseFloat(totalPercentage.toFixed(2))
    },
    totalEmailsCollected() {
      if (!this.targetCampaignUUID || !this.getStatsCompleted) {
        return 42
      }

      return this.allCampaignLeadsByUUID(this.targetCampaignUUID)
    },
  },
  watch: {
    getStatsLoading(loading) {
      if (!loading && this.getStatsCompleted && !this.getStatsError) {
        this.refreshChartData()
      }
    },
  },
  created() {
    if (this.getStatsCompleted && !this.getStatsError) {
      this.refreshChartData()
    }
  },
  methods: {
    refreshChartData() {
      let index = 0
      const newDataSetArray = []
      const targetCampaign = this.targetCampaign
      const targetStats = this.targetStats
      const labelSkipPeriod =
        differenceInDays(
          parseISO(this.analyticsTimeFrame.to),
          parseISO(this.analyticsTimeFrame.from),
        ) > 10
          ? 4
          : 1

      if (!this.targetCampaignUUID) {
        return // No campaign exists
      }

      this.targetCampaignStats.forEach((widgetStats) => {
        newDataSetArray[index] = { ...this.defaultDataSet[index] }
        // Find the matching widget item by id and use its name for the label
        const widgetItem = targetCampaign.widgetItems.find(
          item => item.id === widgetStats.id)

        if (widgetItem) {
          newDataSetArray[index].label = widgetItem.name
        }
        newDataSetArray[index].data = widgetStats[targetStats].data
        index++
      })

      const formattedDateLabels = this.targetCampaignStats[0].labels.map(
        (date) => {
          return this.$parseDate(date)
        })

      formattedDateLabels[0] = ''
      this.dataCollection.labels = formattedDateLabels
      this.dataCollection.datasets = newDataSetArray

      this.chartOptions.scales.xAxes[0].ticks.callback = function (
        tick, index, array) {
        return index % labelSkipPeriod ? '' : tick
      }

      // trigger chart update
      this.chartDataChanged = !this.chartDataChanged
    },
    triggerSetTargetStats(val) {
      this.targetStats = val
      this.refreshChartData()
    },
  },
}
